import * as React from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Stack,
  IconButton,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme, useMediaQuery } from "@mui/material";
import { supabase } from "../logic/client";
import { EmployeeRow, CompanyRow, GENDER_OPTIONS } from "./utils/Utils";
import { useI18n } from "../i18n/I18nProvider";


function initials(first?: string | null, last?: string | null) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "U";
}

type Props = {
  handlePageChange: (page: string) => void;
  role: string;
};

export default function Profile(props: Props) {
  const { handlePageChange, role } = props;
  const { lang } = useI18n();
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [employee, setEmployee] = React.useState<EmployeeRow | null>(null);
  const [company, setCompany] = React.useState<CompanyRow | null>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = React.useState(false);
  const [roles, setRoles] = React.useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const displayName = `${employee?.first_name ?? ""} ${employee?.last_name ?? ""}`.trim() || "User";

  React.useEffect(() => {
    const load = async () => {
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user?.id;
      if (!userId) return;

      const { data: emp } = await supabase
        .from("employees")
        .select("*")
        .eq("auth_user_id", userId)
        .single();

      setEmployee(emp);

      if (emp?.company_id) {
        const { data: comp } = await supabase
          .from("companies")
          .select("id,name")
          .eq("id", emp.company_id)
          .single();

        setCompany(comp);
      }

      if (emp?.avatar_url) {
        const { data } = await supabase.storage
          .from("avatars")
          .createSignedUrl(emp.avatar_url, 3600);

        setAvatarUrl(data?.signedUrl ?? null);
      }

      const { data: roleData } = await supabase
        .from("user_roles_catalog")
        .select("*");

      setRoles(roleData || []);

      if (emp) {
        const { data: assigned } = await supabase
          .from("employee_roles")
          .select("role_id")
          .eq("employee_id", emp.id);

        setSelectedRoles(assigned?.map((r) => r.role_id) || []);
      }
    };

    load();
  }, []);

  const updateField = async (field: keyof EmployeeRow, value: string) => {
    if (!employee) return;

    const payload =
      field === "age"
        ? { [field]: value === "" ? null : Number(value) }
        : { [field]: value };

    const { error } = await supabase
      .from("employees")
      .update(payload)
      .eq("id", employee.id);

    if (!error) {
      setEmployee((prev) =>
        prev
          ? {
              ...prev,
              [field]: field === "age" ? (value === "" ? null : Number(value)) : value,
            }
          : prev
      );
    }
  };

  const uploadAvatar = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await supabase.functions.invoke("set-avatar", {
      body: formData,
    });

    if (data?.avatar_url) {
      const { data: signed } = await supabase.storage
        .from("avatars")
        .createSignedUrl(data.avatar_url, 3600);

      setAvatarUrl(signed?.signedUrl ?? null);
    }
  };

  const removeAvatar = async () => {
    await supabase.functions.invoke("set-avatar", { method: "DELETE" });
    setAvatarUrl(null);
  };

  const deleteAccount = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const token = session?.access_token;

      if (!token) {
        alert("Session expired");
        return;
      }

      // 1. delete all public/user-owned data first
      const { error: rpcError } = await supabase.rpc("delete_my_account");

      if (rpcError) {
        console.error("Public cleanup failed:", rpcError);
        alert("Delete failed");
        return;
      }

      // 2. delete auth user second
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY!,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const text = await res.text();
        console.error("Auth delete failed:", text);
        alert("Auth delete failed");
        return;
      }

      // 3. clear client state
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();

      handlePageChange("WelcomeScreen");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const exportUserData = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        alert(L("Not authenticated", "Nicht angemeldet"));
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/export-user-data`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "takekontrol_resilience_platform_data.json";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(L("Export failed", "Export fehlgeschlagen"));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `
          radial-gradient(circle at 15% 18%, rgba(251, 2, 2, 0.10), transparent 34%),
          radial-gradient(circle at 85% 22%, rgba(255, 0, 0, 0.06), transparent 34%),
          linear-gradient(180deg, #140707 0%, #090909 72%, #050505 100%)
        `,
        px: { xs: 2, sm: 3, md: 4, lg: 5 },
        py: { xs: 2, sm: 3, md: 4 },
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* HOME BUTTON */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 12, sm: 18, md: 24 },
          left: { xs: 12, sm: 18, md: 24 },
          zIndex: 20,
        }}
      >
        <IconButton
          onClick={() => {
            if (role === "admin") {
              handlePageChange("ControlPanel");
            } else {
              handlePageChange("JourneyPillars");
            }
          }}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 0 18px rgba(255,255,255,0.08)",
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <HomeOutlinedIcon />
        </IconButton>
      </Box>

      {/* MAIN AREA */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1480,
          mx: "auto",
          pt: { xs: 7, sm: 8, lg: 4 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 2.5, sm: 3, lg: 4 }}
          alignItems="flex-start"
          sx={{
            width: "100%",
            mx: 0,
          }}
        >
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Stack
                alignItems="center"
                spacing={{ xs: 2, sm: 2.5, lg: 3 }}
                sx={{
                  width: "100%",
                  maxWidth: { xs: 520, lg: 390 },
                  mx: { xs: "auto", lg: 0 },
                  p: { xs: 2.5, sm: 3, lg: 3.5 },
                  borderRadius: 4,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.025))",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    onClick={() => setAvatarMenuOpen((p) => !p)}
                    src={avatarUrl ?? undefined}
                    sx={{
                      width: { xs: 88, sm: 124, lg: 148 },
                      height: { xs: 88, sm: 124, lg: 148 },
                      fontSize: { xs: 30, sm: 40, lg: 48 },
                      fontWeight: 800,
                      bgcolor: "rgba(255,0,0,0.18)",
                      boxShadow: "0 0 42px rgba(255,0,0,0.46)",
                      cursor: "pointer",
                      transition: "0.25s",
                      border: "1px solid rgba(255,255,255,0.14)",
                      "&:hover": {
                        transform: "scale(1.03)",
                        boxShadow: "0 0 44px rgba(255,0,0,0.78)",
                      },
                    }}
                  >
                    {initials(employee?.first_name, employee?.last_name)}
                  </Avatar>

                  {avatarMenuOpen && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: isMobile ? "calc(100% + 12px)" : "50%",
                        left: isMobile ? "50%" : "calc(100% + 18px)",
                        transform: isMobile
                          ? "translateX(-50%)"
                          : "translateY(-50%)",
                        background: "rgba(20,20,20,0.96)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        borderRadius: 2,
                        px: 1.2,
                        py: 1,
                        boxShadow: "0 0 25px rgba(255,0,0,0.42)",
                        backdropFilter: "blur(10px)",
                        zIndex: 10,
                        minWidth: 190,
                      }}
                    >
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => {
                          fileInputRef.current?.click();
                          setAvatarMenuOpen(false);
                        }}
                        sx={{
                          color: "white",
                          justifyContent: "flex-start",
                          textTransform: "none",
                          width: "100%",
                          "&:hover": {
                            background: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        {avatarUrl
                          ? L("Change photo", "Foto ändern")
                          : L("Upload photo", "Foto hochladen")}
                      </Button>

                      {avatarUrl && (
                        <Button
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            removeAvatar();
                            setAvatarMenuOpen(false);
                          }}
                          sx={{
                            color: "#ff5a5a",
                            justifyContent: "flex-start",
                            textTransform: "none",
                            width: "100%",
                            "&:hover": {
                              background: "rgba(255,0,0,0.1)",
                            },
                          }}
                        >
                          {L("Delete photo", "Foto löschen")}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadAvatar(f);
                  }}
                />

                <Box
                  sx={{
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: { xs: "1.35rem", sm: "1.65rem", lg: "1.8rem" },
                      fontWeight: 850,
                      lineHeight: 1.1,
                      mb: 1,
                    }}
                  >
                    {displayName}
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.68)",
                      fontSize: { xs: "0.82rem", sm: "0.95rem" },
                      lineHeight: 1.5,
                    }}
                  >
                    {employee?.position || "—"}
                    {company?.name && (
                      <Box component="span" sx={{ opacity: 0.72 }}>
                        {" "}@ {company.name}
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Stack
                  spacing={1.5}
                  sx={{
                    width: "100%",
                    pt: 1,
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      handlePageChange("WelcomeScreen");
                    }}
                    sx={{
                      minHeight: isTablet ? 52 : 46,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.24)",
                      background: "rgba(255,255,255,0.035)",
                      fontSize: isTablet ? "0.95rem" : "0.85rem",
                      fontWeight: 850,
                      borderRadius: 2.5,
                      letterSpacing: 0,
                      "&:hover": {
                        background: "rgba(255,255,255,0.08)",
                        borderColor: "rgba(255,255,255,0.34)",
                      },
                    }}
                  >
                    {L("Log out", "Abmelden")}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={exportUserData}
                    sx={{
                      minHeight: isTablet ? 52 : 46,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.24)",
                      background: "rgba(255,255,255,0.035)",
                      fontSize: isTablet ? "0.95rem" : "0.85rem",
                      fontWeight: 850,
                      borderRadius: 2.5,
                      letterSpacing: 0,
                      "&:hover": {
                        background: "rgba(255,255,255,0.08)",
                        borderColor: "rgba(255,255,255,0.34)",
                      },
                    }}
                  >
                    {L("Export my data", "Meine Daten exportieren")}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={async () => {
                      const confirmDelete = window.confirm(
                        L(
                          "Are you sure you want to delete your account permanently?",
                          "Möchten Sie Ihr Konto wirklich dauerhaft löschen?"
                        )
                      );

                      if (!confirmDelete) return;

                      await deleteAccount();

                      await supabase.auth.signOut();
                      localStorage.clear();
                      sessionStorage.clear();

                      handlePageChange("WelcomeScreen");
                      window.location.reload();
                    }}
                    sx={{
                      minHeight: isTablet ? 52 : 46,
                      color: "#ff5a5a",
                      borderColor: "rgba(255,0,0,0.42)",
                      background: "rgba(255,0,0,0.02)",
                      fontSize: isTablet ? "0.95rem" : "0.85rem",
                      fontWeight: 850,
                      borderRadius: 2.5,
                      letterSpacing: 0,
                      "&:hover": {
                        background: "rgba(255,0,0,0.1)",
                        borderColor: "red",
                      },
                    }}
                  >
                    {L("Delete my profile", "Profil löschen")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                width: "100%",
                borderRadius: { xs: 0, sm: 4 },
                p: { xs: 0, sm: 2, lg: 2.5 },
                background: {
                  xs: "transparent",
                  sm: "linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.018))",
                },
                border: {
                  xs: "none",
                  sm: "1px solid rgba(255,255,255,0.08)",
                },
                boxShadow: {
                  xs: "none",
                  sm: "0 24px 70px rgba(0,0,0,0.22)",
                },
                backdropFilter: { xs: "none", sm: "blur(10px)" },
              }}
            >
              <Grid
                container
                spacing={{ xs: 1.5, sm: 2 }}
                alignItems="stretch"
                sx={{
                  width: "100%",
                  mx: 0,
                }}
              >
                <Grid item xs={12} sm={6}>
                  <ProfileRow
                    label={L("Age", "Alter")}
                    value={employee?.age?.toString()}
                    field="age"
                    onSave={updateField}
                    isTablet={isTablet}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileRow
                    label={L("Gender", "Geschlecht")}
                    value={employee?.gender?.toString()}
                    field="gender"
                    onSave={updateField}
                    isTablet={isTablet}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileRow
                    label={L("Location", "Ort")}
                    value={employee?.location}
                    field="location"
                    onSave={updateField}
                    isTablet={isTablet}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ProfileRow
                    label={L("Email", "E-Mail")}
                    value={employee?.company_email}
                    field="company_email"
                    onSave={updateField}
                    isTablet={isTablet}
                  />
                </Grid>

                <Grid item xs={12}>
                  {employee && (
                    <RolesRow
                      roles={roles}
                      selectedRoles={selectedRoles}
                      setSelectedRoles={setSelectedRoles}
                      employeeId={employee.id}
                      isTablet={isTablet}
                      lang={lang}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function ProfileRow({
  label,
  value,
  field,
  onSave,
  isTablet = false,
}: {
  label: string;
  value?: string | null;
  field: keyof EmployeeRow;
  onSave: (field: keyof EmployeeRow, value: string) => Promise<void>;
  isTablet?: boolean;
}) {
  const [editing, setEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value ?? "");

  const isGender = field === "gender";

  React.useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: { xs: 82, sm: 112, lg: 126 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderRadius: 2.5,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.028))",
        border: "1px solid rgba(255,255,255,0.10)",
        px: { xs: 1.5, sm: 2.2, lg: 2.5 },
        py: { xs: 1.4, sm: 2.1, lg: 2.4 },
        backdropFilter: "blur(10px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      <Stack spacing={1.2}>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.56)",
            fontSize: { xs: "0.72rem", sm: "0.82rem", lg: "0.88rem" },
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.2,
            minHeight: { xs: 34, sm: 42 },
          }}
        >
          {editing ? (
            <>
              {isGender ? (
                <TextField
                  select
                  value={localValue || ""}
                  onChange={(e) => setLocalValue(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 170,
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      borderRadius: 2,
                    },
                  }}
                >
                  {GENDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label_en}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <input
                  value={localValue}
                  onChange={(e) => setLocalValue(e.target.value)}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "white",
                    borderRadius: 10,
                    padding: isTablet ? "8px 12px" : "6px 10px",
                    fontSize: isTablet ? "15px" : "14px",
                    outline: "none",
                  }}
                />
              )}

              <IconButton
                size="small"
                onClick={async () => {
                  await onSave(field, localValue);
                  setEditing(false);
                }}
                sx={{ color: "#00e676", flexShrink: 0 }}
              >
                ✔
              </IconButton>

              <IconButton
                size="small"
                onClick={() => {
                  setLocalValue(value ?? "");
                  setEditing(false);
                }}
                sx={{ color: "#aaa", flexShrink: 0 }}
              >
                ✖
              </IconButton>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: isTablet ? "1.15rem" : "0.95rem",
                  wordBreak: "break-word",
                  pr: 1,
                  flex: 1,
                  lineHeight: 1.35,
                }}
              >
                {isGender
                  ? GENDER_OPTIONS.find((g) => g.value === value)?.label_en || "—"
                  : value || "—"}
              </Typography>

              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                sx={{ color: "rgba(255,255,255,0.6)", flexShrink: 0 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function RolesRow({
  roles,
  selectedRoles,
  setSelectedRoles,
  employeeId,
  isTablet,
  lang,
}: {
  roles: any[];
  selectedRoles: string[];
  setSelectedRoles: React.Dispatch<React.SetStateAction<string[]>>;
  employeeId: string;
  isTablet: boolean;
  lang: string;
}) {
  const [editing, setEditing] = React.useState(false);

  const toggle = (id: string) => {
    setSelectedRoles((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const save = async () => {
    await supabase
      .from("employee_roles")
      .delete()
      .eq("employee_id", employeeId);

    if (selectedRoles.length > 0) {
      await supabase.from("employee_roles").insert(
        selectedRoles.map((r) => ({
          employee_id: employeeId,
          role_id: r,
        }))
      );
    }

    setEditing(false);
  };

  const selectedLabels = roles
    .filter((r) => selectedRoles.includes(r.id))
    .map((r) => (lang === "de" ? r.label_de : r.label_en))
    .join(", ");

  return (
    <Box
      sx={{
        minHeight: { xs: 98, sm: 120, lg: 132 },
        borderRadius: 2.5,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.028))",
        border: "1px solid rgba(255,255,255,0.10)",
        px: { xs: 1.5, sm: 2.2, lg: 2.5 },
        py: { xs: 1.4, sm: 2.1, lg: 2.4 },
        backdropFilter: "blur(10px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: editing ? 1.5 : 1,
        }}
      >
        <Typography
          sx={{
            color: "rgba(255,255,255,0.56)",
            fontSize: { xs: "0.72rem", sm: "0.82rem", lg: "0.88rem" },
            letterSpacing: "0.02em",
          }}
        >
          {lang === "de" ? "Rollen" : "Roles"}
        </Typography>

        {!editing && (
          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            sx={{ color: "rgba(255,255,255,0.6)" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!editing && (
        <Typography
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: isTablet ? "1.15rem" : "0.95rem",
            lineHeight: 1.6,
          }}
        >
          {selectedLabels || "—"}
        </Typography>
      )}

      {editing && (
        <>
          <Stack spacing={0.6}>
            {roles.map((role) => {
              const checked = selectedRoles.includes(role.id);

              return (
                <Box
                  key={role.id}
                  onClick={() => toggle(role.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    px: 1.2,
                    py: 1,
                    borderRadius: 1.5,
                    "&:hover": {
                      background: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    style={{
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                    }}
                  />

                  <Typography
                    sx={{
                      color: "white",
                      fontSize: isTablet ? "0.95rem" : "0.85rem",
                    }}
                  >
                    {lang === "de" ? role.label_de : role.label_en}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1} mt={1.8}>
            <Button
              size="small"
              onClick={save}
              sx={{
                color: "#00e676",
                fontWeight: 700,
              }}
            >
              ✔ Save
            </Button>

            <Button
              size="small"
              onClick={() => setEditing(false)}
              sx={{
                color: "#aaa",
              }}
            >
              ✖ Cancel
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
